import { useMemo, type CSSProperties } from "react";

import EmScore from "../Survey/EmScore";
import { QRCodeSVG } from "qrcode.react";
import { PersonCheck, Survey, TimeSince } from "../Icons";
import { Card } from "@/types";
import getQuestionsForSurveyType from "@/lib/getQuestionsForSurveyType";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import ProfileImage from "../ProfileImage";
import LogoImage from "../LogoImage";
import { getCardQrUrl } from "@/lib/getCardUrl";
import { FeedbackJourneyLogo, FeedbackJourneyLogoDark } from "../Icons/Logo";
import { captionColor, qrColors } from "@/lib/journeyCardColors";
import DotPattern from "../dots/DotPattern";
import styles from "./JourneyCardMobile.module.css";

type JourneyCardMobileProps = {
  card: Card;
  bgColor: string;
  fgColor: string;
  lightBg: boolean;
};

// About 75 % of the ring round the profile picture, r = 55 in a 116 box.
const RING_LENGTH = 2 * Math.PI * 55;

// The stacked card from the "JourneyCard-Mobile-B" board, for screens under 600 px. No curve and no link in plain text,
// but the dot pattern from the desktop card in a smaller scale.
const JourneyCardMobile = ({ card, bgColor, fgColor, lightBg }: JourneyCardMobileProps) => {
  const qr = qrColors(bgColor, fgColor);
  const d = useDateFormatter();
  const t = useTranslations("card");
  const questions = useMemo(() => getQuestionsForSurveyType(card.surveyType), [card.surveyType]);
  // The public card has no surveyIds, only the stats and the surveys.
  const surveys = card.stats?.noOfSurveys ?? card.surveys?.length ?? card.surveyIds?.split(",").filter(Boolean).length ?? 0;
  const cardUrl = getCardQrUrl(card.cardId);
  const cardStyle = {
    "--journey-card-bg": bgColor,
    "--journey-card-fg": fgColor,
    "--journey-card-caption": captionColor(bgColor, fgColor),
  } as CSSProperties;

  return (
    <div data-testid="journey-card-mobile" className={[styles.card, !lightBg && styles.dark].filter(Boolean).join(" ")} style={cardStyle}>
      <DotPattern className={styles.dots} color={fgColor} width={260} height={170} gap={24} radius={1.7} />
      <div className={styles.identity}>
        <span className={styles.portrait}>
          <svg className={styles.ring} viewBox='0 0 116 116' aria-hidden='true' focusable='false'>
            <circle cx='58' cy='58' r='55' fill='none' strokeWidth='3' strokeLinecap='round' strokeDasharray={`${RING_LENGTH * 0.75} ${RING_LENGTH}`} />
          </svg>
          <ProfileImage src={card.image || ""} alt={card.candidate} variant='journey-card-mobile' className={styles.photo} />
        </span>
        <div className={styles.titles}>
          <h1 className={styles.name}>{card.candidate}</h1>
          <p className={styles.title}>{card.title}</p>
          {(card.logo || card.cardTitle) && (
            <p className={styles.muted}>
              {card.logo && <LogoImage src={card.logo} alt='Company logo' maxHeight={20} maxWidth={80} />}
              {card.cardTitle && <span>{card.cardTitle}</span>}
            </p>
          )}
        </div>
      </div>
      <div className={styles.scores}>
        <EmScore inside={t("totalScoreLabel")} score={card.totalScore} size={7} />
        <div className={styles.questions}>
          {questions.map((q, i) => (
            <EmScore key={`${q}_${i}`} after={t(`${q}Label`)} size={3.5} score={(card as any)[q]} />
          ))}
        </div>
      </div>
      <ul className={styles.pills}>
        <li className={styles.pill} data-testid="journey-card-mobile-pill">
          <PersonCheck width='14px' height='14px' />
          {t("answersPill", card.responders)}
        </li>
        <li className={styles.pill} data-testid="journey-card-mobile-pill">
          <TimeSince width='14px' height='14px' />
          {t("latestPill", d(card.latestResponse))}
        </li>
        <li className={styles.pill} data-testid="journey-card-mobile-pill">
          <Survey width='14px' height='14px' />
          {t("surveysPill", surveys)}
        </li>
      </ul>
      <div className={styles.footer}>
        <div className={styles.qrCode} style={{ background: qr.bgColor }}>
          <QRCodeSVG bgColor={qr.bgColor} fgColor={qr.fgColor} height='44' width='44' value={cardUrl || "<thisrandomstringmakesthislookcool!>"} />
        </div>
        <div className={styles.verified}>
          <strong>{t("verifiedCard")}</strong>
          <span className={styles.caption}>{t("scanToVerify")}</span>
        </div>
        <span className={styles.logo}>{lightBg ? <FeedbackJourneyLogoDark w={120} /> : <FeedbackJourneyLogo w={120} />}</span>
      </div>
    </div>
  );
};

export default JourneyCardMobile;
